/**
 * Apparence des formats d'exercice (onglets, badges) — partagée entre la
 * carte exerciseur (ExerciseurCard) et la carte de question de banque
 * (BanqueQuestionCard). Clé = libellé renvoyé par getQuestionTypeLabel /
 * FORMAT_LABELS des générateurs.
 */
export const FORMAT_CONFIG = {
    'QCM':           { icon: '◧', border: 'border-blue-500',   bg: 'bg-blue-50',   text: 'text-blue-600',   badge: 'bg-blue-500' },
    'QCM Multi':     { icon: '◨', border: 'border-purple-500', bg: 'bg-purple-50',  text: 'text-purple-600', badge: 'bg-purple-500' },
    'Saisie':        { icon: '⌨', border: 'border-teal-500',   bg: 'bg-teal-50',    text: 'text-teal-600',   badge: 'bg-teal-500' },
    'Vrai/Faux':     { icon: '◑', border: 'border-amber-500',  bg: 'bg-amber-50',   text: 'text-amber-600',  badge: 'bg-amber-500' },
    'Tableau':       { icon: '◑', border: 'border-amber-500',  bg: 'bg-amber-50',   text: 'text-amber-600',  badge: 'bg-amber-500' },
    'Relier':        { icon: '⤮', border: 'border-green-500',  bg: 'bg-green-50',   text: 'text-green-600',  badge: 'bg-green-500' },
    'Classer':       { icon: '☷', border: 'border-indigo-500', bg: 'bg-indigo-50',  text: 'text-indigo-600', badge: 'bg-indigo-500' },
    'Ordonner':      { icon: '↕', border: 'border-pink-500',   bg: 'bg-pink-50',    text: 'text-pink-600',   badge: 'bg-pink-500' },
    'Texte à trous': { icon: '▭', border: 'border-orange-500', bg: 'bg-orange-50',  text: 'text-orange-600', badge: 'bg-orange-500' },
    'Drag':          { icon: '↕', border: 'border-indigo-500', bg: 'bg-indigo-50',  text: 'text-indigo-600', badge: 'bg-indigo-500' },
    'Saisie trous':  { icon: '▭', border: 'border-orange-500', bg: 'bg-orange-50',  text: 'text-orange-600', badge: 'bg-orange-500' },
};

export const DEFAULT_FORMAT = { icon: '•', border: 'border-gray-400', bg: 'bg-gray-50', text: 'text-gray-600', badge: 'bg-gray-500' };

/** Article féminin/masculin pour le bouton "Ajouter ce/cette" */
export const getArticle = (format) => ['Saisie', 'Saisie trous', 'Droite graduée', 'Construction', 'Horloge'].includes(format) ? 'cette' : 'ce';

/** Date courte (12/06/2026) */
export const formatDateCourte = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};

/** Date jour/mois (« 2026-09-15 » → « 15/09 ») */
export const formatJourMois = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

/** « 3 attendus », « 1 attendu » */
export const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

// Un bloc (paragraphe, titre, liste, cellule, <br>) vaut une espace : « 8⁶⁰ ? (Observe… », pas « ?(Observe… ».
export const stripHtml = (s) => (s || '')
  .replace(/<\/?(p|div|br|li|ul|ol|h[1-6]|table|tr|td|th)\b[^>]*>/gi, ' ')
  .replace(/<[^>]*>/g, '')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * Formules `$…$` mises en texte simple, pour un nom accessible ou une
 * infobulle (le rendu visible passe par MathText) : « $3 \times 3$ » →
 * « 3 × 3 », « $\dfrac{1}{2}$ » → « 1/2 », « $10^{-4}$ » → « 10^-4 ».
 */
const SYMBOLES_TEX = { times: '×', div: '÷', cdot: '·', le: '≤', leq: '≤', ge: '≥', geq: '≥', neq: '≠', approx: '≈', pi: 'π', infty: '∞' };
export const texteLisible = (s) => (s || '').replace(/\$([^$]*)\$/g, (_, f) => f
    .replace(/\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g, '$1/$2')
    .replace(/\\([a-zA-Z]+)/g, (m, nom) => (SYMBOLES_TEX[nom] ? ` ${SYMBOLES_TEX[nom]} ` : ''))
    .replace(/\^\{([^{}]*)\}/g, '^$1')
    .replace(/[{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim());

/** Début de l'énoncé d'un exercice en texte simple, pour un nom accessible (panier, groupes). */
export const apercuTexte = (payload) => texteLisible(stripHtml(payload?.texte)).slice(0, 60) || 'Exercice';

/**
 * Une ligne de banque peut arriver avec `contenu` / `elements_additionnels`
 * en chaîne JSON (anciennes lignes) : on rend des objets, sans muter l'entrée.
 */
const parseJsonSiChaine = (v) => {
    if (typeof v !== 'string') return v;
    try { return JSON.parse(v); } catch { return v; }
};

export const normaliserQuestionBanque = (q) => {
    if (!q) return q;
    const contenu = parseJsonSiChaine(q.contenu);
    const elements = parseJsonSiChaine(q.elements_additionnels);
    if (contenu === q.contenu && elements === q.elements_additionnels) return q;
    return { ...q, contenu, elements_additionnels: elements };
};
